import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  useConversation,
  useConversations,
  useCreateConversation,
  useCreateDispatch,
  useDispatches,
  useIdeSessionsPage,
} from "~/api/queries";
import { ApprovalCards } from "~/features/console/ApprovalCards";
import { Composer, type DispatchMode } from "~/features/console/Composer";
import { MachineRail } from "~/features/console/MachineRail";
import { Transcript } from "~/features/console/Transcript";
import { Button } from "~/foundation/components/Button";
import { Empty } from "~/foundation/components/Empty";
import { Text } from "~/foundation/components/Text";

/** The literal prefix that makes a conversation a remote session rather than a document thread. */
const REMOTE_PREFIX = "Remote: ";

/**
 * Hold a working discussion with a connected editor, answer its approvals, run the machine's
 * flows — live.
 *
 * "Live" here is **three polls, no socket** (§E-3): the active thread at 5 s while a reply is
 * pending, the dispatch list at 10 s while this section is mounted, and the window list at
 * 60 s. kms has all three as well — they are its FALLBACK layer under a SignalR connection to
 * TomatoFlow's hub, which is cookie-authenticated while slate carries a PKCE bearer. Making
 * that hub take a bearer is unverified backend work, so the socket is deferred and the cost is
 * stated on the surface rather than hidden: approvals raised INSIDE a conversation cannot be
 * answered here, only the ones a dispatched flow parks on.
 */
export function RemoteSessionsPanel() {
  const { t } = useTranslation();
  const [mode, setMode] = useState<DispatchMode>("chat");
  const [target, setTarget] = useState("");
  const [draft, setDraft] = useState("");
  const [activeId, setActiveId] = useState<string | null>(null);
  const [awaitingReply, setAwaitingReply] = useState(false);

  const { data: sessionsPage } = useIdeSessionsPage();
  const { data: conversations } = useConversations();
  const { data: thread } = useConversation(activeId, awaitingReply);
  // 10 s while this section is mounted — kms's own standing cadence here, faster than the
  // 15 s the Runs section is happy with, because this is the surface somebody watches.
  const { data: dispatches } = useDispatches();
  const createConversation = useCreateConversation();
  const createDispatch = useCreateDispatch();

  const threads = useMemo(
    () =>
      (conversations ?? []).filter((conversation) => conversation.title.startsWith(REMOTE_PREFIX)),
    [conversations],
  );

  const activeDispatches = useMemo(
    () =>
      (dispatches ?? []).filter(
        (dispatch) => activeId !== null && dispatch.conversationId === activeId,
      ),
    [dispatches, activeId],
  );

  const workingStatus =
    activeDispatches.find((dispatch) => ["queued", "claimed", "running"].includes(dispatch.status))
      ?.statusDetail ?? null;

  const send = async () => {
    const prompt = draft.trim();
    if (!prompt) {
      return;
    }
    try {
      if (mode === "thought") {
        await createDispatch.mutateAsync({
          kind: "thought",
          agent: "ClaudeCode",
          payload: { prompt },
          ...(target ? { targetSessionKey: target } : {}),
        });
        toast.success(t("Task dispatched to your IDE"));
      } else {
        // A discussion needs somewhere to be held; the thread is created first so the
        // dispatch can name it, and reused for every follow-up.
        let conversationId = activeId;
        if (!conversationId) {
          const created = await createConversation.mutateAsync({
            title: `${REMOTE_PREFIX}${prompt.slice(0, 60)}`,
          });
          conversationId = created.id;
          setActiveId(created.id);
        }
        await createDispatch.mutateAsync({
          kind: "chat",
          agent: "ClaudeCode",
          payload: { prompt, conversationId },
          conversationId,
          ...(target ? { targetSessionKey: target } : {}),
        });
      }
      setDraft("");
      setAwaitingReply(true);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t("Could not reach your IDE"));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Text variant="small">
        {t(
          "Hold working discussions with a connected IDE, answer its approvals, and run the machine's flows — live. Everything is recorded.",
        )}
      </Text>

      <Composer
        mode={mode}
        onModeChange={setMode}
        target={target}
        onTargetChange={setTarget}
        value={draft}
        onChange={setDraft}
        onSend={() => void send()}
        sending={createDispatch.isPending || createConversation.isPending}
        sessions={sessionsPage?.sessions ?? []}
      />

      <div className="grid gap-4 lg:grid-cols-[200px_1fr_260px]">
        <nav className="flex flex-col gap-1">
          <h3 className="text-body font-medium text-text">{t("Discussions")}</h3>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setActiveId(null);
              setAwaitingReply(false);
            }}
          >
            {t("+ New discussion")}
          </Button>
          {threads.map((conversation) => (
            <button
              key={conversation.id}
              type="button"
              onClick={() => setActiveId(conversation.id)}
              className={`rounded-sm px-2 py-1 text-start text-small ${
                activeId === conversation.id ? "bg-bg-secondary text-text" : "text-text-secondary"
              }`}
            >
              <span className="block truncate">
                {conversation.title.slice(REMOTE_PREFIX.length)}
              </span>
              {conversation.flowId ? (
                <span className="text-small text-text-tertiary">{t("flow")}</span>
              ) : null}
            </button>
          ))}
        </nav>

        <div className="flex min-w-0 flex-col gap-3">
          <ApprovalCards dispatches={dispatches ?? []} />
          {/*
            The half of the approval story slate cannot serve, said plainly rather than shown
            as a button that does nothing.
          */}
          <p className="text-small text-text-tertiary">
            {t("Approvals raised inside a conversation are answered in the IDE for now.")}
          </p>
          {thread ? (
            <Transcript
              messages={thread.messages}
              awaitingReply={awaitingReply}
              workingStatus={workingStatus}
            />
          ) : (
            <Empty message={t("Pick a discussion, or start one above.")} />
          )}
        </div>

        <MachineRail targetSessionKey={target} dispatches={dispatches ?? []} />
      </div>
    </div>
  );
}
